import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Fade } from "react-awesome-reveal";
import HeroSmall from "../../components/HeroSmall";
import SEO from "../../utils/SEO";
import bg from "../../assets/bgblue.webp";
import styles from "./Events.module.scss";

import { LanguageContext } from "../../context/LanguageContext";

const eventTypes = [
  {
    icon: "💍",
    no: { title: "Bryllup", desc: "Et personlig irisportrett til hver gjest er et vakkert og varig minne fra bryllupsdagen." },
    en: { title: "Weddings", desc: "A personal iris portrait for every guest is a beautiful and lasting memory from the wedding day." },
  },
  {
    icon: "🎂",
    no: { title: "Bursdager", desc: "Et irisportrett er en unik gave gjestene kan ta med hjem – perfekt for bursdager i alle størrelser." },
    en: { title: "Birthdays", desc: "An iris portrait is a unique keepsake guests can take home — perfect for birthdays of all sizes." },
  },
  {
    icon: "🏢",
    no: { title: "Firmaarrangementer", desc: "Et profesjonelt og engasjerende innslag for kick-off, julebord og teambuilding." },
    en: { title: "Corporate Events", desc: "A professional and engaging addition to kick-offs, Christmas parties, and team building." },
  },
  {
    icon: "🎓",
    no: { title: "Konfirmasjoner & Dimmisjoner", desc: "Et fint minne fra en viktig dag – til hver gjest." },
    en: { title: "Confirmations & Graduations", desc: "A meaningful keepsake from an important occasion — for every guest." },
  },
];

const includedItems = [
  {
    no: "Fotografering av alle gjester på stedet",
    en: "On-site photography for all guests",
  },
  {
    no: "Høyoppløselig digital fil til alle gjester",
    en: "High-resolution digital file for every guest",
  },
  {
    no: "Dedikert fotograf gjennom hele arrangementet",
    en: "Dedicated photographer throughout the event",
  },
];

function Events() {
  const { language } = useContext(LanguageContext);
  const no = language === "no";

  return (
    <>
      <SEO
        title={no ? "Arrangementer" : "Events"}
        description={
          no
            ? "Book Instant Iris til ditt arrangement – bryllup, bursdager, firmafester og mer. Fast pris med irisportrett til hver gjest inkludert."
            : "Book Instant Iris for your event – weddings, birthdays, corporate parties and more. Fixed price with an iris portrait for every guest included."
        }
      />
      <main className="overflow-hidden">
        <HeroSmall
          bg={bg}
          title={no ? "Instant Iris til ditt arrangement" : "Instant Iris for Your Event"}
        />

        {/* Intro */}
        <section className={styles.intro_section}>
          <div className="container">
            <div className="wrap">
              <Fade direction="up" triggerOnce>
                <div className={styles.intro_inner}>
                  <h2 className={styles.section_heading}>
                    {no
                      ? "Et irisportrett til hver gjest – til én fast pris"
                      : "An iris portrait for every guest — at one fixed price"}
                  </h2>
                  <p className={styles.lead}>
                    {no
                      ? "Instant Iris setter opp en fotograferingsstasjon på ditt arrangement og tar irisportrett av alle gjestene – til én fast pris. En personlig og profesjonell opplevelse som alle kan ta med seg hjem."
                      : "Instant Iris sets up a photography station at your event and captures an iris portrait of every guest — at one fixed price. A personal and professional experience that everyone gets to take home."}
                  </p>
                </div>
              </Fade>
            </div>
          </div>
        </section>

        {/* Event Types */}
        <section className={styles.types_section}>
          <div className="container">
            <div className="wrap">
              <Fade direction="up" triggerOnce>
                <h2 className={styles.section_heading}>
                  {no ? "Passer til alle anledninger" : "Suitable for all occasions"}
                </h2>
              </Fade>
              <div className={styles.cards_grid}>
                {eventTypes.map((event, i) => (
                  <Fade key={i} direction="up" delay={i * 80} triggerOnce>
                    <div className={styles.event_card}>
                      <span className={styles.event_icon}>{event.icon}</span>
                      <h4 className={styles.card_title}>
                        {no ? event.no.title : event.en.title}
                      </h4>
                      <p className={styles.card_desc}>
                        {no ? event.no.desc : event.en.desc}
                      </p>
                    </div>
                  </Fade>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className={styles.included_section}>
          <div className="container">
            <div className="wrap">
              <div className={styles.included_inner}>
                <Fade direction="left" triggerOnce>
                  <div className={styles.included_text}>
                    <h2 className={styles.section_heading}>
                      {no ? "Hva er inkludert?" : "What's included?"}
                    </h2>
                    <p>
                      {no
                        ? "Du betaler én fast pris for arrangementet. Ingen skjulte gebyrer, ingen tilleggskostnader per gjest."
                        : "You pay one fixed price for the event. No hidden fees, no per-guest surcharges."}
                    </p>
                    <ul className={styles.included_list}>
                      {includedItems.map((item, i) => (
                        <li key={i} className={styles.included_item}>
                          <span className={styles.check}>✓</span>
                          {no ? item.no : item.en}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Fade>
                <Fade direction="right" triggerOnce>
                  <div className={styles.included_highlight}>
                    <p className={styles.highlight_label}>
                      {no ? "Pris" : "Pricing"}
                    </p>
                    <p className={styles.highlight_price}>
                      {no ? "Ta kontakt for tilbud" : "Contact us for a quote"}
                    </p>
                    <p className={styles.highlight_sub}>
                      {no
                        ? "Prisen tilpasses antall gjester og varighet."
                        : "Pricing is tailored to guest count and duration."}
                    </p>
                    <Link to="/contact" className={styles.cta_button}>
                      {no ? "Be om tilbud" : "Request a quote"}
                    </Link>
                  </div>
                </Fade>
              </div>
            </div>
          </div>
        </section>

        {/* Discounted Products */}
        <section className={styles.upgrade_section}>
          <div className="container">
            <div className="wrap">
              <Fade direction="up" triggerOnce>
                <h2 className={styles.section_heading}>
                  {no ? "20% rabatt på produkter" : "20% off products"}
                </h2>
                <p className={styles.upgrade_intro}>
                  {no
                    ? "Gjester som ønsker å kjøpe fysiske produkter utover den inkluderte digitale filen, får 20% rabatt på arrangementet. Se vårt fulle produktutvalg på produktsiden."
                    : "Guests who wish to purchase physical products beyond the included digital file receive a 20% discount at the event. See our full product range on the products page."}
                </p>
                <Link to="/products" className={styles.cta_button}>
                  {no ? "Se produkter" : "View products"}
                </Link>
              </Fade>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={styles.cta_section}>
          <div className="container">
            <div className="wrap">
              <Fade direction="up" triggerOnce>
                <div className={styles.cta_inner}>
                  <h2>
                    {no
                      ? "Interessert? Ta kontakt."
                      : "Interested? Get in touch."}
                  </h2>
                  <p>
                    {no
                      ? "Kontakt oss for å høre mer om tilgjengelighet og priser."
                      : "Contact us to learn more about availability and pricing."}
                  </p>
                  <Link to="/contact" className={styles.cta_button}>
                    {no ? "Kontakt oss" : "Contact us"}
                  </Link>
                </div>
              </Fade>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default Events;
